import { useState } from 'react';
import { Heart, Shield, GraduationCap, Utensils, Sun, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const features = [
  { icon: Heart, title: 'Loving Care', description: 'Our experienced caregivers provide nurturing attention to each child, creating a warm and supportive atmosphere.', color: 'primary', bg: 'bg-primary/8' },
  { icon: Shield, title: 'Safe Environment', description: 'We maintain the highest safety standards with secure facilities, regular safety checks, and trained staff.', color: 'success', bg: 'bg-success/8' },
  { icon: GraduationCap, title: 'Quality Education', description: 'Age-appropriate curriculum designed to foster cognitive, social, and emotional development.', color: 'secondary', bg: 'bg-secondary/8' },
  { icon: Utensils, title: 'Healthy Nutrition', description: 'Nutritious meals and snacks prepared daily to support growing bodies and minds.', color: 'accent', bg: 'bg-accent/8' },
  { icon: Sun, title: 'Outdoor Activities', description: 'Daily outdoor play and activities to promote physical health and connection with nature.', color: 'primary', bg: 'bg-primary/8' },
  { icon: Users, title: 'Small Class Sizes', description: 'Low child-to-teacher ratios ensure personalized attention and meaningful interactions.', color: 'secondary', bg: 'bg-secondary/8' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const FeaturesSection = () => {
  const [activeCard, setActiveCard] = useState(null);

  const toggleCard = (index) => {
    setActiveCard(activeCard === index ? null : index);
  };

  return (
    <section className="py-10 md:py-24 bg-background relative" data-testid="features-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16 space-y-4"
        >
          <span className="inline-block bg-accent/15 text-accent-foreground text-sm font-bold px-5 py-2 rounded-full tracking-wide uppercase">
            Why Choose Us
          </span>
          <h2 className="font-fredoka font-bold text-4xl sm:text-5xl text-foreground" data-testid="features-heading">
            Why Choose Hooyo's Place?
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            We provide everything your child needs to thrive in a supportive and engaging environment.
          </p>
        </motion.div>

        {/* 2-Column Grid on Mobile, 3-Column on Desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isMiddleCol = index % 3 === 1;
            const isActive = activeCard === index;
            return (
              <motion.div
                key={index}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-30px" }}
                variants={cardVariants}
                whileHover={{ scale: 1.03, y: -5 }}
                onClick={() => toggleCard(index)}
                className={`${feature.bg} border-2 border-${feature.color}/12 rounded-2xl md:rounded-[2rem] p-4 md:p-8 space-y-2 md:space-y-4 cursor-pointer select-none transition-all duration-300 ${isMiddleCol ? 'lg:translate-y-6' : ''} ${isActive ? `border-${feature.color}/40 shadow-soft` : ''} sm:cursor-default`}
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-${feature.color}/15 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 md:w-7 md:h-7 text-${feature.color}`} />
                </div>
                <h3 className="font-fredoka font-bold text-sm md:text-xl text-foreground">
                  {feature.title}
                </h3>
                {/* Always visible on desktop */}
                <p className="text-xs md:text-base text-muted-foreground leading-relaxed hidden sm:block">
                  {feature.description}
                </p>
                {/* Expandable on mobile */}
                <AnimatePresence>
                  {isActive && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="text-xs text-muted-foreground leading-relaxed sm:hidden overflow-hidden"
                    >
                      {feature.description}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
